import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import fp from 'lodash/fp';
import { Skeleton } from '@mui/material';
import VisibilitySensor from 'react-visibility-sensor';

import { useDesktop, Stack, useMediaSwitch } from 'Modules/ui3';

import { ContentBox } from '../ContentBox';
import { WithUsPaginationElement } from '../../api';
import { SlideBaseProps } from '../Slider/types';


const flexGap = 2;
const PartnerSlideContainer: FC<PropsWithChildren<{isBig?: boolean}>> = ({ children, isBig }) => (
    <ContentBox
        minHeight={isBig ? '152px' : '80px'}
        flex={1}
        display='flex'
        justifyContent='center'
        alignItems='center'
        borderRadius={isBig ? 8 : 6}
    >
        {children}
    </ContentBox>
);

export interface PartnerSlideProps extends SlideBaseProps<WithUsPaginationElement[]> {
    needShow?: () => void;
    rowDirection?: boolean;
}

export const PartnerSlide: FC<PartnerSlideProps> = ({ data, needShow, rowDirection }) => {
    const onVisible = useCallback(isVisible => isVisible && needShow && needShow(), [needShow]);
    const isDesktop = useDesktop();
    const columns = useMediaSwitch(4, 3, 1);
    const imgKey = useMemo(() => isDesktop ? 'big' : 'small', [isDesktop]);

    return (
        <Stack
            direction={rowDirection ? 'row' : 'column'}
            gap={flexGap}
            flexWrap='wrap'
            maxHeight={isDesktop ? '312px' : '168px'}
            alignContent='flex-start'
            sx={({ spacing }) => ({
                '& > div': {
                    [rowDirection ? 'flexBasis' : 'width']:
                        `calc((100% - ${spacing(flexGap * (columns - 1))}) / ${columns})`,
                },
            })}
        >
            {data.map((partner, idx) => fp.isEmpty(partner) ? (
                <VisibilitySensor key={`slideIndex_${idx}`} onChange={onVisible}>
                    <PartnerSlideContainer isBig={isDesktop}>
                        <Skeleton width='60%' height='50px' />
                    </PartnerSlideContainer>
                </VisibilitySensor>
            ) : (
                <PartnerSlideContainer key={idx} isBig={isDesktop}>
                    <img
                        src={partner[imgKey].file.url}
                        alt={partner.name || 'partnerImg'}
                        width={`${partner[imgKey].width}px`}
                        height={`${partner[imgKey].height}px`}
                    />
                </PartnerSlideContainer>
            ))}
        </Stack>
    );
};
